import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Injector,
} from '@angular/core';
import Quill, { Delta } from 'quill';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import './custom-hr-blot';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { Subject } from 'rxjs';
import { startCase } from 'lodash';
import { brMatcher, inputMatcher, setAngularInjector } from './custom-hr-blot';
@Component({
  imports: [CommonModule, ReactiveFormsModule, QuillEditorComponent],
  selector: 'rmp-html-editor',
  standalone: true,
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: HtmlEditorComponent,
      multi: true,
    },
  ],
})
export class HtmlEditorComponent
  implements OnDestroy, OnInit, ControlValueAccessor
{
  @Input() formGroup!: FormGroup;
  @Input() formControlName!: string;
  @Input() label: string = '';
  @Input() maxContentLength = 0;
  @Input() minWidth: string = '596px';
  @Input() maxWidth: string = '596px';
  @Input() minHeight: string = '150px';
  private destroy$ = new Subject<void>();

  @ViewChild('editor', { static: true }) editor!: QuillEditorComponent;
  formControl: FormControl = new FormControl(null);
  useCustomToolbar: boolean = true;
  quillModules = {
    keyboard: {
      bindings: {
        'shift enter': {
          key: 'Enter',
          shiftKey: true,
          handler: this.shiftEnterHandler.bind(this),
        },
      },
    },
    clipboard: {
      matchers: [
        ['br', brMatcher.bind(this)],
        ['input', inputMatcher.bind(this)],
      ],
    },
  };

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private injector: Injector,
  ) {
    // Set the Angular injector for DateInputBlot
    setAngularInjector(this.injector);
  }

  ngOnInit(): void {
    this.formControl = this.formGroup?.get(this.formControlName) as FormControl;
  }

  shiftEnterHandler(this: any, range: any, context: any) {
    const quill = this.editor.quillEditor;
    const currentLeaf = quill.getLeaf(range.index)[0];
    const nextLeaf = quill.getLeaf(range.index + 1)[0];
    const isInOrderedList =
      currentLeaf.parent && currentLeaf.parent.domNode.tagName === 'LI';

    if (isInOrderedList) {
      quill.insertEmbed(range.index, 'softbreak', true, 'USER');
    } else {
      quill.insertEmbed(range.index, 'softbreak', true);
      // Insert a second break if:
      // At the end of the editor, OR next leaf has a different parent (<p>)
      if (nextLeaf === null || currentLeaf.parent !== nextLeaf.parent) {
        quill.insertEmbed(range.index, 'softbreak', true, 'USER');
      }
    }
    // Now that we've inserted a line break, move the cursor forward
    quill.setSelection(range.index + 1);
    return false;
  }

  writeValue(value: string): void {
    //this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    //this.disabled = isDisabled;
  }
  undo() {
    this.editor.quillEditor.history.undo();
  }
  redo() {
    this.editor.quillEditor.history.redo();
  }
  GetContentLength() {
    const quillEditor = this.editor.quillEditor;
    const contentLength =
      quillEditor?.getText().trim().length === 0
        ? 0
        : quillEditor?.getLength() || 0;
    return contentLength;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  GetError(): string {
    const control = this.formGroup?.controls[this.formControlName];
    let errorMessage = '';
    for (const key in control?.errors) {
      if (control?.errors[key]) {
        errorMessage = this.getErrorMessage(key, control?.errors[key], control);
        break;
      }
    }
    return errorMessage;
  }
  getErrorMessage(
    validationErrorType: string,
    validationError: any,
    control: AbstractControl<any, any>,
  ): string {
    const label = this.GetLabel();
    switch (validationErrorType) {
      case 'required':
        return `${label} is required`;
      case 'minlength':
        return `${label} must be at least ${control.errors?.['minlength'].requiredLength} characters long`;
      case 'maxlength':
        return `${label} must be less than ${control.errors?.['maxlength'].requiredLength} characters long`;
      case 'pattern':
        return `${label} is not valid`;
      default:
        return `${label} is invalid`;
    }
  }
  hasError(): boolean {
    const control = this.formGroup?.controls[this.formControlName];
    const invalid = !!(control && this.formGroup.dirty && control.invalid);

    return invalid;
  }
  GetLabel() {
    return this.label || startCase(this.formControlName);
  }

  handleContentChange(content: any) {
    const quillEditor = this.editor.quillEditor;
    const contentLength = this.GetContentLength();
    const maxLength = this.maxContentLength;
    if (maxLength > 0 && contentLength > maxLength) {
      quillEditor.deleteText(maxLength, contentLength);
      quillEditor.setContents(
        quillEditor.getContents().slice(0, maxLength - 1),
      );
      quillEditor.setSelection(maxLength - 1, Quill.sources.SILENT);
      this.formControl.setValue(quillEditor.root.innerHTML.trim(), {
        emitEvent: false,
      });
    }
    if (contentLength === 0) {
      this.formControl.setValue('', { emitEvent: false });
    }
    //importatnt to get the semantic html and set on form control
    //to maintainsome html elements that otherwise get removed
    // e.g. <br> tags within a list item of TOU
    if (content.source === 'USER') {
      const html = quillEditor.getSemanticHTML();
      this.formControl.setValue(html, { emitEvent: false });
    }
    this.onChange(this.formControl.value);
  }
}
